import IndividualAffordabilityPanel from './IndividualAffordabilityPanel';
import { securityTypeLabel } from '../../../constants/securityTypes';
import { capturedAffordability, currentAffordabilityInput } from '../../../services/individualAffordability';
import React from 'react';
import IndividualLoanSummary from './IndividualLoanSummary';
import { useFormContext } from 'react-hook-form';
import { IndividualRiskBadge, type IndividualCreditAssessment } from './IndividualCreditCheckPanel';
import { describeChargeFrequency } from './DefaultTermsFields';
import {marriageRegimes,maritalCategory} from '../individualMaritalStatus';

const formatEnumLabel = (value?: string) =>
  value
    ? value.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
    : '-';

interface ReviewStepProps {
  individualCreditAssessment?: IndividualCreditAssessment | null;
  creditCheckPanel?: React.ReactNode;
  statementIqPanel?: React.ReactNode;
}

export default function ReviewStep({ individualCreditAssessment, creditCheckPanel, statementIqPanel }: ReviewStepProps) {
  const { watch } = useFormContext();
  const formData = watch();
  const customerType = watch('customerType');
  const loanType = watch('loanType');

  // Helper function to format currency
  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return 'N/A';
    return `R ${value.toLocaleString()}`;
  };

  // Add a function to format the loan type name for display
  const formatLoanType = (loanType: string | undefined): string => {
    if (!loanType) return 'N/A';

    switch (loanType) {
      case 'PAYDAY':
        return 'Payday Loan';
      case 'PERSONAL':
        return 'Personal Loan';
      case 'BUSINESS':
        return 'BUSINESS LOAN';
      case 'HOME':
        return 'Home Loan';
      case 'VEHICLE':
        return 'Vehicle Loan';
      default:
        return loanType;
    }
  };

  return (
    <div className="form-section">
      <h6 className="form-section-title">Review Application</h6>
      <p className="text-muted mb-4">
        Review all client information before submitting the application. Verify that all required fields are complete and accurate.
      </p>

      {customerType === 'individual' && <IndividualAffordabilityPanel financial={capturedAffordability(formData)} idType={formData.individual?.idType}
        pricing={currentAffordabilityInput(formData.loan)}
        version={formData.loan.individualPricingQuote?.pricing.versionId} />}
      <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">Application Type</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-12 mb-2">
              <strong>Loan Type:</strong> {formatLoanType(formData.loanType)}
            </div>
            <div className="col-12 mb-2">
              <strong>Customer Type:</strong> {customerType === 'individual' ? 'Individual' : 'Company'}
            </div>
          </div>
        </div>
      </div>

      {customerType === 'individual' && statementIqPanel}

      {customerType === 'individual' && (creditCheckPanel || (
        <div className="card mb-3">
          <div className="card-header bg-light">
            <h6 className="mb-0">Credit bureau pre-screen</h6>
          </div>
          <div className="card-body">
            {individualCreditAssessment ? (
              <div className="row">
                <div className="col-md-6 mb-2">
                  <strong>Recommendation:</strong> {formatEnumLabel(individualCreditAssessment.result.recommendation)}
                </div>
                <div className="col-md-6 mb-2 d-flex align-items-center gap-2">
                  <strong>Risk profile:</strong> <IndividualRiskBadge result={individualCreditAssessment.result} />
                </div>
                <div className="col-12 small text-muted">
                  XDS and scoring completed for this application session. This is not final approval.
                </div>
              </div>
            ) : (
              <div className="text-warning">Not run in this application session.</div>
            )}
          </div>
        </div>
      ))}

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">Customer Information</h6>
        </div>
        <div className="card-body">
          <div className="row">
            {customerType === 'individual' && formData.individual && (
              <>
                <div className="col-md-6 mb-2">
                  <strong>Full Name:</strong> {formData.individual.fullNames}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{formData.individual.idType === 'PASSPORT' ? 'Passport Number:' : 'ID Number:'}</strong> {formData.individual.idNumber}
                  {formData.individual.idType === 'PASSPORT' && <div><strong>Passport Expiry Date:</strong> {formData.individual.passportExpiryDate}</div>}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Marital Status:</strong> {formatEnumLabel(maritalCategory(formData.individual.maritalStatus))}
                </div>
                {maritalCategory(formData.individual.maritalStatus)==='MARRIED' && <div className="col-md-6 mb-2">
                  <strong>Marital Regime:</strong> {marriageRegimes.find(regime=>regime.value===formData.individual.maritalStatus)?.label || 'Not selected'}
                </div>}
                <div className="col-md-6 mb-2">
                  <strong>Previously disadvantaged individual:</strong> {formData.individual.isPreviouslyDisadvantaged == null ? 'Not recorded' : formData.individual.isPreviouslyDisadvantaged ? 'Yes' : 'No'}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Occupation:</strong> {formData.individual.occupation}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Employer / Income Source:</strong> {['PERMANENT', 'CONTRACT'].includes(formData.individual.employmentType)
                    ? formData.individual.employer : formData.individual.incomeSource}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Employment Type:</strong> {formatEnumLabel(formData.individual.employmentType)}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Monthly Net Income (declared):</strong> {formData.individual.monthlyNetIncome == null ? 'Not captured' : formatCurrency(formData.individual.monthlyNetIncome)}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Monthly Living Expenses (declared):</strong> {formData.individual.monthlyLivingExpenses == null ? 'Not captured' : formatCurrency(formData.individual.monthlyLivingExpenses)}
                </div>
                <div className="col-12 mb-2"><strong>Other living cost details:</strong>
                  {formData.financial?.otherLivingExpenseItems == null ? <span> Not recorded; total {formData.financial?.otherLivingExpenses == null ? 'not captured' : formatCurrency(formData.financial.otherLivingExpenses)}</span>
                    : formData.financial.otherLivingExpenseItems.length === 0 ? <span> No other costs</span>
                    : <ul className="mb-0">{formData.financial.otherLivingExpenseItems.map((item: any, index: number) => <li key={index}>{item.name}: {formatCurrency(item.amount)} per month</li>)}</ul>}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Date of Birth:</strong> {formData.individual.dateOfBirth || 'Not captured'}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Dependents:</strong> {formData.individual.dependents}
                </div>
                <div className="col-12 mt-2">
                  <strong>Address:</strong> {[formData.individual.address?.streetAddress,
                    formData.individual.address?.city, formData.individual.address?.province,
                    formData.individual.address?.postalCode, formData.individual.address?.country]
                    .filter(Boolean).join(', ')}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Primary Phone:</strong> {formData.individual.contactDetails?.primaryPhone}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Email:</strong> {formData.individual.contactDetails?.email}
                </div>
              </>
            )}

            {customerType === 'company' && formData.company && (
              <>
                <div className="col-md-6 mb-2">
                  <strong>Company Name:</strong> {formData.company.companyName}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Registration Number:</strong> {formData.company.companyRegNumber}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Tax Number:</strong> {formData.company.taxNumber}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>VAT Number:</strong> {formData.company.vatNumber || 'N/A'}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Contact Person:</strong> {formData.company.contactPersonFirstName} {formData.company.contactPersonLastName}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Contact Phone:</strong> {formData.company.contactTel}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Contact Email:</strong> {formData.company.contactEmail}
                </div>
                {formData.company.representativeOccupation && <div className="col-md-6 mb-2">
                  <strong>Representative Occupation:</strong> {formData.company.representativeOccupation}
                </div>}
                <div className="col-md-6 mb-2">
                  <strong>Annual Turnover:</strong> {formatCurrency(formData.company.turnover)}
                </div>
                <div className="col-12 mt-2">
                  <strong>Address:</strong> {formData.company.address?.lineOne}
                  {formData.company.address?.lineTwo && `, ${formData.company.address.lineTwo}`},
                  {formData.company.address?.suburb}, {formData.company.address?.city}, {formData.company.address?.code}
                </div>
                <div className="col-12 mt-3">
                  <strong>Directors:</strong>
                  <ul className="list-group mt-2">
                    {formData.company.directors?.map((director: any, index: number) => (
                      <li key={index} className="list-group-item">
                        {director.firstName} {director.lastName} (ID: {director.idNumber})
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {customerType !== 'individual' && <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">Funding Request</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>Funding product:</strong> {formatEnumLabel((formData as any).loan?.fundingType)}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Amount requested:</strong> {formatCurrency(Number((formData as any).loan?.loanAmount || 0))}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Required funding date:</strong> {(formData as any).loan?.requiredFundingDate || '-'}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Proposed repayment date:</strong> {(formData as any).loan?.proposedRepaymentDate || '-'}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Customer contribution:</strong>{' '}
              {(formData as any).loan?.customerContribution
                ? formatCurrency(Number((formData as any).loan.customerContribution))
                : '-'}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Primary repayment source:</strong> {(formData as any).loan?.primaryRepaymentSource || '-'}
            </div>
            {loanType !== 'PERSONAL' && <><div className="col-md-6 mb-2">
              <strong>Security available:</strong> {formatEnumLabel((formData as any).loan?.securityAvailability)}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Security offered:</strong>{' '}
              {((formData as any).loan?.securityTypesOffered || []).length
                ? (formData as any).loan.securityTypesOffered.map(securityTypeLabel).join(', ')
                : '-'}
            </div></>}
          </div>
        </div>
      </div>}

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">Bank Account Information</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>Account Holder:</strong> {formData.bankAccount?.accountHolderName}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Bank Name:</strong> {formData.bankAccount?.bankName}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Account Number:</strong> {formData.bankAccount?.accountNumber}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Account Type:</strong> {formData.bankAccount?.accountType}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Branch Code:</strong> {formData.bankAccount?.branchCode}
            </div>
          </div>
        </div>
      </div>

      {loanType === 'PERSONAL' ? <IndividualLoanSummary loan={formData.loan} /> : <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">Loan Details</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>Loan Type:</strong> {formatLoanType(formData.loanType)}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Loan Amount:</strong> {formatCurrency(formData.loan?.loanAmount)}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Loan Term:</strong> {formData.loan?.loanTerm} days
            </div>
            <div className="col-md-6 mb-2">
              <strong>Security Type:</strong> {formData.loan?.securityType || 'N/A'}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Calculation Mode:</strong> {formData.loan?.calculationMode || 'N/A'}
            </div>
            {formData.loan?.customLoanType && (
              <div className="col-md-6 mb-2">
                <strong>Custom Loan Type:</strong> {formData.loan.customLoanType}
              </div>
            )}
            {formData.loan?.interestRate && (
              <div className="col-md-6 mb-2">
                <strong>Interest Rate:</strong> {formData.loan.interestRate.toFixed(2)}%
              </div>
            )}
            {formData.loan?.adminFee && (
              <div className="col-md-6 mb-2">
                <strong>Admin Fee:</strong> {formatCurrency(formData.loan.adminFee)}
              </div>
            )}
            <div className="col-md-6 mb-2">
              <strong>Default Terms:</strong>{' '}
              {formData.loan?.defaultInterestRate != null
                ? `${formData.loan.defaultInterestRate}% per period`
                : 'Loan interest rate'}
              {', '}
              {describeChargeFrequency(formData.loan?.defaultChargeFrequency, formData.loan?.defaultChargeIntervalDays)}
              {', '}
              {formData.loan?.defaultGraceDays ? `${formData.loan.defaultGraceDays}-day grace` : 'no grace period'}
            </div>
            {formData.loan?.interestAmount && (
              <div className="col-md-6 mb-2">
                <strong>Interest Amount:</strong> {formatCurrency(formData.loan.interestAmount)}
              </div>
            )}
            {formData.loan?.totalRepayment && (
              <div className="col-md-6 mb-2">
                <strong>Total Repayment:</strong> {formatCurrency(formData.loan.totalRepayment)}
              </div>
            )}
            {formData.loan?.dueDate && (
              <div className="col-md-6 mb-2">
                <strong>Due Date:</strong> {new Date(formData.loan.dueDate).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            )}
            {formData.loan?.commencementDate && (
              <div className="col-md-6 mb-2">
                <strong>Commencement Date:</strong> {new Date(formData.loan.commencementDate).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            )}
            {formData.loan?.numberOfInstallments && (
              <div className="col-md-6 mb-2">
                <strong>Number of Installments:</strong> {formData.loan.numberOfInstallments}
              </div>
            )}
            {formData.loan?.paymentDates && formData.loan.paymentDates.length > 0 && (
              <div className="col-12 mt-2">
                <strong>Payment Schedule:</strong>
                <ul className="list-group mt-2">
                  {formData.loan.paymentDates.map((date: string, index: number) => (
                    <li key={index} className="list-group-item">
                      Installment {index + 1}: {new Date(date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="col-12 mt-2">
              <strong>Loan Purpose:</strong>
              <p className="mt-1">{formData.loan?.loanPurpose}</p>
            </div>
          </div>
        </div>
      </div>

      }
      {loanType !== 'PERSONAL' && formData.collaterals && formData.collaterals.length > 0 && (
        <div className="card mb-3">
          <div className="card-header bg-light">
            <h6 className="mb-0">Collateral Information</h6>
          </div>
          <div className="card-body">
            {formData.collaterals.map((collateral: any, collateralIndex: number) => (
              <div key={collateralIndex} className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">Collateral #{collateralIndex + 1}</h6>
                <div className="row">
                  <div className="col-12 mb-2">
                    <strong>Asset Description:</strong> {collateral.assetDescription}
                  </div>
                  {collateral.make && (
                    <div className="col-md-6 mb-2">
                      <strong>Make/Brand:</strong> {collateral.make}
                    </div>
                  )}
                  {collateral.yearModel && (
                    <div className="col-md-6 mb-2">
                      <strong>Year/Model:</strong> {collateral.yearModel}
                    </div>
                  )}
                  {collateral.registrationNumber && (
                    <div className="col-md-6 mb-2">
                      <strong>Registration Number:</strong> {collateral.registrationNumber}
                    </div>
                  )}
                  <div className="col-md-6 mb-2">
                    <strong>Owner Name:</strong> {collateral.ownerName}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Owner ID:</strong> {collateral.ownerId}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Estimated Value:</strong> {formatCurrency(collateral.value)}
                  </div>
                  {collateral.otherDetails && (
                    <div className="col-12 mt-2">
                      <strong>Other Details:</strong>
                      <p className="mt-1">{collateral.otherDetails}</p>
                    </div>
                  )}
                </div>
                {collateralIndex < formData.collaterals.length - 1 && <hr className="my-3" />}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">Uploaded Documents</h6>
        </div>
        <div className="card-body">
          {formData.documents && formData.documents.length > 0 ? (
            <ul className="list-group">
              {formData.documents.map((doc: any, index: number) => (
                <li key={index} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{doc.documentType.replace(/_/g, ' ')}</strong>
                      {doc.documentKey && <div className="text-muted small">{doc.documentKey}</div>}
                    </div>
                    <span className="badge bg-success">Uploaded</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="alert alert-warning">
              No documents have been uploaded yet.
            </div>
          )}
        </div>
      </div>

      <div className="alert alert-info">
        <div className="d-flex">
          <div className="me-3">
            <i className="fa fa-info-circle fa-lg"></i>
          </div>
          <div>
            <p className="mb-0">
              <strong>Please Note:</strong> By submitting this application, you confirm that all information provided is accurate and complete.
              False information may result in rejection of your application or legal consequences.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
